import { useMemo } from "react";
import SEO from "../components/SEO/SEO";
import { breadcrumbLd, productLd } from "../seo/jsonLd";
import { getPrimaryProductImageUrl } from "../utils/imageUtils";
import {
  Breadcrumb,
  ProductImageGallery,
  ProductInfo,
  ProductTabs,
  ProductDetailLayout,
  StickyAddToCartBar,
  // ProductFAQ,
  // ProductReviews,
  // AskQuestion,
} from "../components/ProductDetail";
import RecommendedProducts from "../components/Products/RecommendedProducts";
import { useProductDetail } from "../hooks/useProductDetail";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";

const DynamicProductDetail = () => {
  const {
    // Data
    product,
    loading,
    error,
    quantity,
    cartError,
    cartLoading,
    cartSuccess,
    mainImage,
    selectedTab,
    // recommendedProducts,
    // recommendedLoading,
    isWishlisted,
    wishlistLoading,
    user,

    // Computed values
    originalPrice,
    discountedPrice,
    hasDiscount,

    // Variant state
    selectedOptions,
    selectedSku,

    // Handlers
    handleQuantityChange,
    handleAddToCart,
    handleOptionSelect,
    handleWishlistClick,
    handleImageChange,
    handleTabChange,
    handleBack,
    handleBackToProducts,
  } = useProductDetail();

  // Recently viewed products
  const productForHistory = useMemo(
    () =>
      product
        ? {
            ...product,
            price: product.usd_price || 0,
            image: mainImage || product.image_paths?.[0] || "",
            description: product.description || "",
            name: product.name || "",
          }
        : null,
    [product, mainImage],
  );

  // @ts-ignore - fixing type mismatch between local and global Product types
  const recentlyViewed = useRecentlyViewed(productForHistory);

  const productImageAbs = product
    ? mainImage && mainImage.startsWith("http")
      ? mainImage
      : getPrimaryProductImageUrl(product.image_paths)
    : "";

  const productJsonLd = product
    ? [
        productLd({
          id: product.id,
          name: product.name || "Product",
          description: product.description,
          image: productImageAbs,
          brandName: product.brand_name,
          price: hasDiscount ? discountedPrice : originalPrice,
          inStock: product.has_variants
            ? !!selectedSku && (selectedSku.is_unlimited_stock || selectedSku.stock_status !== "out_of_stock")
            : product.is_unlimited_stock || (product.quantity ?? 0) > 0,
          averageRating: (product as any).average_rating,
          reviewCount: (product as any).review_count,
          sku: selectedSku?.sku ?? product.id,
        }),
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Products", path: "/products" },
          { name: product.name || "Product", path: `/product/${product.id}` },
        ]),
      ]
    : undefined;

  return (
    <ProductDetailLayout
      loading={loading}
      error={error || (!product ? "Product not found" : null)}
      onBackToProducts={handleBackToProducts}
    >
      {product && (
        <>
          <SEO
            title={product.name || "Product"}
            description={(product.description || "").slice(0, 160) || `Buy ${product.name} at My Perfume.`}
            image={productImageAbs}
            canonicalPath={`/product/${product.id}`}
            type="product"
            jsonLd={productJsonLd}
          />
          <Breadcrumb onBack={handleBack} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <ProductImageGallery
              product={product}
              mainImage={mainImage}
              onImageChange={handleImageChange}
            />

            <ProductInfo
              product={product}
              hasDiscount={hasDiscount}
              originalPrice={originalPrice}
              discountedPrice={discountedPrice}
              quantity={quantity}
              onQuantityChange={handleQuantityChange}
              onAddToCart={handleAddToCart}
              cartError={cartError}
              cartLoading={cartLoading}
              cartSuccess={cartSuccess}
              onWishlistClick={handleWishlistClick}
              isWishlisted={isWishlisted}
              wishlistLoading={wishlistLoading}
              user={user}
              selectedOptions={selectedOptions}
              selectedSku={selectedSku}
              onOptionSelect={handleOptionSelect}
            />
          </div>

          <ProductTabs
            product={product}
            selectedTab={selectedTab}
            onTabChange={handleTabChange}
            hasDiscount={hasDiscount}
            originalPrice={originalPrice}
          />

          {/* Reviews Section */}
          {/* <ProductReviews productId={product.id} /> */}

          {/* Ask a Question */}
          {/* <AskQuestion
            productName={product.name || "this product"}
            productId={product.id}
          /> */}

          {/* FAQ Section */}
          {/* <ProductFAQ /> */}

          {/* <RecommendedProducts
            products={recommendedProducts}
            loading={recommendedLoading}
            title="You Might Also Like"
          /> */}

          {/* Recently Viewed Products */}
          {recentlyViewed.length > 0 && (
            <RecommendedProducts
              products={recentlyViewed}
              loading={false}
              title="Recently Viewed"
            />
          )}

          {/* Sticky Add to Cart Bar */}
          <StickyAddToCartBar
            productName={product.name || "Product"}
            price={originalPrice}
            hasDiscount={hasDiscount}
            discountedPrice={discountedPrice}
            onAddToCart={handleAddToCart}
            isOutOfStock={
              product.has_variants
                ? !!selectedSku &&
                  !selectedSku.is_unlimited_stock &&
                  (selectedSku.stock_status === "out_of_stock" ||
                    (selectedSku.quantity ?? 0) === 0)
                : !product.is_unlimited_stock && (product.quantity || 0) === 0
            }
            needsVariantSelection={!!product.has_variants && !selectedSku}
          />
        </>
      )}
    </ProductDetailLayout>
  );
};

export default DynamicProductDetail;
